import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Polygon, Polyline, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';
import { findPlace } from '../../utils/placeSearch';
import { tcpLayout, ROAD_TYPES, SETUPS, SPEEDS, STANDARDS, tcpReference } from '../../utils/tcpLayout';
import JobSearchSelect from '../JobSearchSelect';
import { drawSign, signPng, SIGN_CATALOG } from '../../utils/tcpSigns';
import { fetchStreets, streetsNear, traceApproach, crossingsAlong, centroid, shiftRoad } from '../../utils/tcpRoad';
import { jobNumber } from '../../utils/ui';

// 🚧 Traffic Control (Ryan, Oct 30 2026) — a site-specific traffic control plan on the satellite:
// outline the work area, trace the lane traffic comes in on, pick the setup and speed, and the signs,
// cone taper, buffer and arrow board are laid out from the MUTCD Part 6 numbers (src/utils/tcpLayout.js).
// Anything can be dragged. Print makes the PDF — satellite, distances, signs, notes, and the ATSSA
// person typed in on this drawing — and files it on the job's Documents.

const IMAGERY = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const RENO = [39.5296, -119.8138];
const box = { padding: '7px 9px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#fff', width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, margin: '10px 0 4px' };
const btn = (bg, fg = '#fff', border = bg) => ({ padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${border}`, background: bg, color: fg, fontSize: 13, fontWeight: 800, cursor: 'pointer' });

export default function TrafficControl({ onBack }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState('');
  const load = () => apiFetch('/traffic-plans').then(d => setList(Array.isArray(d) ? d : [])).catch(e => setErr(e?.error || 'Could not load the drawings.'));
  useEffect(() => { load(); }, []);
  async function create() {
    try { const p = await apiFetch('/traffic-plans', { method: 'POST', body: JSON.stringify({ title: 'New traffic control plan', speed: 35, road_type: 'urban_low', lane_width: 12, shoulder_width: 8 }) }); setOpenId(p.id); }
    catch (e) { setErr(e?.error || 'Could not start a drawing.'); }
  }
  if (openId) return <Editor id={openId} onBack={() => { setOpenId(null); load(); }} />;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <button onClick={onBack} style={btn('#fff', '#111', '#e5e7eb')}>‹ Schedule</button>
        <h1 style={{ fontSize: 21, fontWeight: 800, margin: 0 }}>🚧 Traffic Control</h1>
        <button onClick={create} style={{ ...btn('#e87722'), marginLeft: 'auto' }}>+ New drawing</button>
      </div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
        Site-specific traffic control plans drawn on the satellite — signs, cone taper and buffer laid out from the MUTCD Part 6 distances, ready to print for NDOT and city permits. The certified person still checks and signs every drawing.
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {!list ? <div style={{ color: '#9ca3af' }}>Loading…</div> : !list.length ? (
        <div style={{ color: '#9ca3af', fontSize: 14, padding: 20, background: '#fff', borderRadius: 10, border: '1px dashed #e5e7eb' }}>No drawings yet. Press <b>+ New drawing</b> to start one.</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
          {list.map(p => (
            <div key={p.id} role="button" tabIndex={0} onClick={() => setOpenId(p.id)}
              style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '11px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}>
              <b style={{ fontSize: 14, flex: 1 }}>{p.title}</b>
              <span style={{ fontSize: 12.5, color: '#374151' }}>{p.project_number ? `${p.project_number} — ${p.project_name || ''}` : 'No job'}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{(SETUPS[p.setup] || {}).label || ''} · {p.speed || '—'} mph</span>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: p.standard ? '#1e3a5f' : '#b45309' }}>{p.standard ? (STANDARDS[p.standard] || {}).label : 'Standard not picked'}</span>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Map pieces as small HTML icons, so they can be dragged.
const icons = {
  cone: L.divIcon({ className: '', iconSize: [12, 12], iconAnchor: [6, 6], html: '<div style="width:10px;height:10px;border-radius:50%;background:#f97316;border:1.5px solid #fff;box-shadow:0 0 2px #000"></div>' }),
  arrow: L.divIcon({ className: '', iconSize: [44, 18], iconAnchor: [22, 9], html: '<div style="width:42px;height:16px;background:#111;border:1px solid #facc15;color:#facc15;font:800 11px sans-serif;text-align:center;line-height:16px">&gt;&gt;&gt;</div>' }),
  // The sign face itself (src/utils/tcpSigns.js), not its code — `px` grows with the zoom so the words read.
  sign: (code, text, px = 40) => L.divIcon({ className: '', iconSize: [px, px], iconAnchor: [px / 2, px / 2], html: `<img src="${signPng(code, text, Math.round(px * 2.2))}" style="width:${px}px;height:${px}px;display:block" alt="${code}" />` }),
  drum: L.divIcon({ className: '', iconSize: [14, 14], iconAnchor: [7, 7], html: '<div style="width:12px;height:12px;border-radius:50%;background:repeating-linear-gradient(0deg,#f97316 0 3px,#fff 3px 6px);border:1.5px solid #111"></div>' }),
  barricade: L.divIcon({ className: '', iconSize: [34, 12], iconAnchor: [17, 6], html: '<div style="width:32px;height:10px;background:repeating-linear-gradient(45deg,#f97316 0 5px,#fff 5px 10px);border:1.5px solid #111"></div>' }),
  label: text => L.divIcon({ className: '', iconSize: [110, 18], iconAnchor: [55, 9], html: `<div style="display:inline-block;background:rgba(255,255,255,.92);border:1px solid #1e3a5f;border-radius:4px;padding:0 5px;font:700 11px sans-serif;color:#1e3a5f;white-space:nowrap">${text}</div>` }),
  dot: L.divIcon({ className: '', iconSize: [10, 10], iconAnchor: [5, 5], html: '<div style="width:8px;height:8px;border-radius:50%;background:#fff;border:2px solid #2563eb"></div>' }),
};

function ClickCatcher({ mode, onPoint, onEmpty }) {
  useMapEvents({ click(e) { if (mode) onPoint([e.latlng.lat, e.latlng.lng]); else if (onEmpty) onEmpty(); } });
  return null;
}
// Sign size on the screen by map zoom: small when looking at the whole layout, big enough to read up close.
const signPx = z => (z >= 20 ? 84 : z >= 19 ? 64 : z >= 18 ? 48 : 36);
// What can be added by hand (+ Add device).
const DEVICES = [
  { key: 'cone', label: 'Cone', type: 'cone' },
  { key: 'drum', label: 'Drum', type: 'drum' },
  { key: 'barricade', label: 'Type III barricade', type: 'barricade' },
  { key: 'arrow', label: 'Arrow board', type: 'arrow' },
  ...SIGN_CATALOG.map(([code, text]) => ({ key: `sign:${code}`, label: `${code} — ${text}`, type: 'sign', code, text })),
];
const DEVICE_NAME = { cone: 'Cone', drum: 'Drum', barricade: 'Type III barricade', arrow: 'Arrow board' };
function iconFor(it, selected, zoom) {
  if (it.type === 'sign') {
    const px = signPx(zoom);
    if (!selected) return icons.sign(it.code, it.text, px);
    return L.divIcon({ className: '', iconSize: [px + 8, px + 8], iconAnchor: [px / 2 + 4, px / 2 + 4], html: `<div style="padding:2px;border:2px solid #2563eb;border-radius:6px;background:rgba(37,99,235,.15)"><img src="${signPng(it.code, it.text, Math.round(px * 2.2))}" style="width:${px}px;height:${px}px;display:block" /></div>` });
  }
  const base = it.type === 'cone' ? icons.cone : it.type === 'drum' ? icons.drum : it.type === 'barricade' ? icons.barricade : it.type === 'arrow' ? icons.arrow : icons.label(it.text);
  if (!selected) return base;
  const o = base.options;
  return L.divIcon({ className: '', iconSize: [o.iconSize[0] + 10, o.iconSize[1] + 10], iconAnchor: [o.iconAnchor[0] + 5, o.iconAnchor[1] + 5],
    html: `<div style="padding:3px;border:2px solid #2563eb;border-radius:6px;background:rgba(37,99,235,.18);display:inline-block">${o.html}</div>` });
}
function MapGrab({ onMap, onView }) {
  const map = useMap();
  useEffect(() => { onMap(map); }, [map]); // eslint-disable-line react-hooks/exhaustive-deps
  useMapEvents({ moveend() { const c = map.getCenter(); onView([c.lat, c.lng], map.getZoom()); } });
  return null;
}

function Editor({ id, onBack }) {
  const [plan, setPlan] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [coords, setCoords] = useState({});
  const [mode, setMode] = useState(null);   // 'area' | 'road' | null
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [search, setSearch] = useState('');
  const [ai, setAi] = useState(null);       // the 🤖 AI plan's answer, shown in the panel
  const [check, setCheck] = useState(null); // the 🤖 Check plan's answer
  const [sel, setSel] = useState(null);     // the device clicked on (id) — Duplicate / Delete
  const [adding, setAdding] = useState(null); // the device being added by clicking the map (a DEVICES entry)
  const streetsRef = useRef(null);         // { at: [lat,lng], ways } — the street map around the work
  const checkFrame = useRef(null);         // the AI check's picture frame (picture spot → map) for its fixes
  const [undoFix, setUndoFix] = useState(null); // the drawing before ✓ Apply fixes
  const mapRef = useRef(null);
  const saveTimer = useRef(null);
  const pending = useRef({});

  useEffect(() => {
    apiFetch(`/traffic-plans/${id}`).then(setPlan).catch(e => setErr(e?.error || 'Could not open the drawing.'));
    // In number order, newest first: jobs, then work orders, then anything without a real number (tests).
    const rank = j => { const n = String(jobNumber(j) || ''); return /^\d/.test(n) ? 0 : /^WO\s?\d/i.test(n) ? 1 : 2; };
    apiFetch('/projects').then(d => setJobs((Array.isArray(d) ? d : []).slice()
      .sort((a, b) => rank(a) - rank(b) || String(jobNumber(b) || '').localeCompare(String(jobNumber(a) || ''), undefined, { numeric: true })))).catch(() => {});
    apiFetch('/map/data').then(d => setCoords(Object.fromEntries((d?.jobs || []).map(j => [j.id, [j.lat, j.lng]])))).catch(() => {});
  }, [id]);

  // Every change saves itself a moment later.
  function change(patch) {
    setPlan(p => ({ ...p, ...patch }));
    pending.current = { ...pending.current, ...patch };
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const body = pending.current; pending.current = {};
      apiFetch(`/traffic-plans/${id}`, { method: 'PUT', body: JSON.stringify(body) }).catch(e => setErr(e?.error || 'That did not save.'));
    }, 600);
  }
  useEffect(() => () => {   // leaving: send whatever is still waiting
    clearTimeout(saveTimer.current);
    if (Object.keys(pending.current).length) apiFetch(`/traffic-plans/${id}`, { method: 'PUT', body: JSON.stringify(pending.current) }).catch(() => {});
  }, [id]);

  const layout = useMemo(() => {
    if (!plan) return null;
    return tcpLayout({ road: plan.road, area: plan.area, setup: plan.setup, sideOf: plan.side, speed: plan.speed, roadType: plan.road_type,
      laneWidth: Number(plan.lane_width) || 12, shoulderWidth: Number(plan.shoulder_width) || 8, noRightTurn: !!plan.no_right_turn });
  }, [plan]);
  // moved[id] = [lat,lng] when dragged, null when deleted; extras = devices added or duplicated by hand.
  const placed = useMemo(() => {
    if (!plan) return [];
    const mv = plan.moved || {};
    const fromLayout = layout ? layout.items.filter(it => mv[it.id] !== null).map(it => ({ ...it, ll: mv[it.id] || it.ll })) : [];
    return [...fromLayout, ...(Array.isArray(plan.extras) ? plan.extras : []).map(x => ({ ...x, extra: true }))];
  }, [layout, plan]);
  // Delete key removes the selected device (not while typing in a box).
  const delRef = useRef(null);
  useEffect(() => {
    const onKey = e => { if ((e.key === 'Delete' || e.key === 'Backspace') && !/INPUT|TEXTAREA|SELECT/.test(e.target.tagName) && delRef.current) { e.preventDefault(); delRef.current(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (err && !plan) return <div style={{ padding: 30, color: '#b91c1c' }}>{err}</div>;
  if (!plan) return <div style={{ padding: 30, color: '#9ca3af' }}>Opening the drawing…</div>;

  const center = plan.center_lat != null ? [plan.center_lat, plan.center_lng] : RENO;
  const area = Array.isArray(plan.area) ? plan.area : [];
  const road = Array.isArray(plan.road) ? plan.road : [];
  const extras = Array.isArray(plan.extras) ? plan.extras : [];
  const newId = () => `x${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
  const addPoint = ll => {
    if (mode === 'area') change({ area: [...area, ll] });
    else if (mode === 'road') change({ road: [...road, ll] });
    else if (mode === 'add' && adding) {
      const it = { id: newId(), type: adding.type, ll, ...(adding.type === 'sign' ? { code: adding.code, text: adding.text } : null) };
      change({ extras: [...extras, it] }); setSel(it.id);
    }
  };
  const selected = sel ? placed.find(it => it.id === sel) : null;
  const moveDevice = (it, ll) => {
    if (it.extra) change({ extras: extras.map(x => (x.id === it.id ? { ...x, ll } : x)) });
    else change({ moved: { ...(plan.moved || {}), [it.id]: ll } });
  };
  function duplicate() {
    if (!selected) return;
    const { extra, d, ...copy } = selected;   // eslint-disable-line no-unused-vars
    const it = { ...copy, id: newId(), ll: [selected.ll[0] + 0.00008, selected.ll[1] + 0.00003] };   // ~30 ft off, to drag into place
    change({ extras: [...extras, it] }); setSel(it.id);
  }
  function remove() {
    if (!selected) return;
    if (selected.extra) change({ extras: extras.filter(x => x.id !== selected.id) });
    else change({ moved: { ...(plan.moved || {}), [selected.id]: null } });
    setSel(null);
  }
  delRef.current = selected ? remove : null;
  const deviceName = it => (it.type === 'sign' ? `${it.code} — ${it.text}` : it.type === 'label' ? `Label "${it.text}"` : DEVICE_NAME[it.type] || it.type);
  // The signs as they stand on the drawing (for the PDF's sign list, the load-up list and the AI check).
  const signsNow = () => placed.filter(it => it.type === 'sign').map(it => ({ code: it.code, text: it.text,
    ...(it.d && !it.extra && /^sign\d$/.test(it.id) ? { d: it.d } : { where: it.id === 'nrt' ? 'at the turn into the work area' : it.id === 'end' ? 'past the end of the work' : 'placed on the drawing' }) }))
    // approaching order: the warning signs farthest first, then No Right Turn, signs added by hand, END ROAD WORK
    .sort((a, b) => { const r = x => (x.d ? 0 : /turn/.test(x.where) ? 1 : /past the end/.test(x.where) ? 3 : 2); return r(a) - r(b) || (b.d || 0) - (a.d || 0); });
  const pickJob = jid => {
    const j = jobs.find(x => x.id === jid);
    // Job names usually start with the number already ("2534 - RTC …") — don't say it twice.
    const jobName = j ? (String(j.name || '').startsWith(String(j.project_number || '~')) ? j.name : `${j.project_number || ''} ${j.name || ''}`.trim()) : '';
    change({ project_id: jid || null, ...(j && (!plan.title || plan.title === 'New traffic control plan') ? { title: jobName } : null) });
    if (coords[jid] && mapRef.current) mapRef.current.setView(coords[jid], 18);
  };
  async function go() {
    if (!search.trim()) return;
    setBusy('search');
    const hit = await findPlace(search.trim());
    setBusy('');
    if (!hit) { setErr('Could not find that place — try the street and city.'); return; }
    setErr('');
    if (mapRef.current) mapRef.current.setView([hit.lat, hit.lng], 19);
    if (!plan.location) change({ location: hit.display.split(',').slice(0, 3).join(',').trim() });
  }
  // The load-up list for page 2: every sign on the drawing (approaching order), cones, arrow board —
  // with each sign's picture, so the PDF shows the sign rather than its code.
  function loadList() {
    const signs = signsNow();
    const count = t => placed.filter(it => it.type === t).length;
    return {
      signs: signs.map(s => ({ code: s.code, text: s.text })),
      cones: count('cone'), arrows: count('arrow'), drums: count('drum'), barricades: count('barricade'),
      images: Object.fromEntries(signs.map(s => [s.code, signPng(s.code, s.text, 160)])),
    };
  }
  async function print() {
    if (!plan.standard) { setErr('Pick the standard first — ATSSA or the NDOT Standard Plans (top of the settings on the right).'); return; }
    if (!layout) { setErr('Trace the road first — the layout is worked out along it.'); return; }
    setBusy('print'); setErr('');
    try {
      clearTimeout(saveTimer.current);
      if (Object.keys(pending.current).length) { await apiFetch(`/traffic-plans/${id}`, { method: 'PUT', body: JSON.stringify(pending.current) }); pending.current = {}; }
      const image = await snapshot(mapRef.current, area, placed);
      const res = await fetch(`${API_BASE}/traffic-plans/${id}/pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken() },
        body: JSON.stringify({ image, num: layout.num, workLen: layout.workLen, signs: signsNow(), load: loadList() }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || 'The PDF did not come back.'); }
      const url = URL.createObjectURL(await res.blob());
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) { setErr(e.message || 'Could not print it.'); }
    setBusy('');
  }
  // ── 🤖 AI (Ryan, Oct 30 2026): the AI picks the street and the setup; the street map traces the road;
  // the MUTCD arithmetic still places everything. Then it can check the finished plan.
  async function streetMap() {
    const c = centroid(area), had = streetsRef.current;
    if (had && Math.hypot((had.at[0] - c[0]) * 364000, (had.at[1] - c[1]) * 281000) < 300) return had.ways;
    const ways = await fetchStreets(area, apiFetch);
    streetsRef.current = { at: c, ways };
    return ways;
  }
  const addNote = line => {
    const cur = String(plan.notes || '');
    if (!line || cur.toLowerCase().includes(String(line).toLowerCase())) return;
    change({ notes: cur ? `${cur.trimEnd()}\n${line}` : line });
  };
  async function aiPlan() {
    if (area.length < 3) { setErr('Outline the work area first (①) — the AI plans around it.'); return; }
    setBusy('ai-map'); setErr(''); setCheck(null);
    try {
      const ways = await streetMap();
      setBusy('ai');
      const near = streetsNear(area, ways);
      if (!near.length) throw new Error('No street on the street map near this work area — trace the road by hand (②).');
      const image = await aiPicture(mapRef.current, area, near, ways);
      const r = await apiFetch(`/traffic-plans/${id}/ai-plan`, { method: 'POST', body: JSON.stringify({ image,
        streets: near.map(({ n, name, highway, lanes, oneway, maxspeed_mph, centre_line_ft }) => ({ n, name, highway, lanes, oneway, maxspeed_mph, centre_line_ft })) }) });
      const st = near.find(x => x.n === r.street) || near[0];
      const t = traceApproach({ area, ways, wayId: st.wayId, setup: r.setup, side: r.side, speed: r.speed, roadType: r.road_type,
        laneWidth: Number(plan.lane_width) || 12, shoulderWidth: Number(plan.shoulder_width) || 8 });
      change({ setup: r.setup, side: r.side, speed: r.speed, road_type: r.road_type, no_right_turn: r.no_right_turn ? 1 : 0,
        ...(t ? { road: t.road } : null), moved: {}, ...(!plan.location ? { location: st.name } : null) });
      if (r.note) addNote(r.note);
      setAi({ ...r, streetName: st.name, streetKey: st.key, direction: t?.direction, traced: !!t });
    } catch (e) { setErr(e?.error || e?.message || 'The AI plan did not work this time.'); }
    setBusy('');
  }
  async function aiCheck() {
    if (!layout) { setErr('There is no plan to check yet — outline the work and trace the road (or press 🤖 AI plan).'); return; }
    setBusy('check'); setErr('');
    try {
      let crossings = [];
      try {
        const ways = await streetMap();
        const near = streetsNear(area, ways);
        const main = (ai?.streetKey && near.find(x => x.key === ai.streetKey)) || near[0];
        crossings = crossingsAlong(road, area, ways, main?.key);
      } catch { crossings = []; }   // the check still runs on the picture alone
      const frame = {};
      const image = await snapshot(mapRef.current, area, placed, frame);
      // Every device with where it sits on the picture, so the AI can say which to move or remove.
      const devices = placed.filter(it => it.type !== 'label').map(it => { const [x, y] = frame.P(it.ll); return { id: it.id, what: deviceName(it), x: Math.round(x), y: Math.round(y) }; })
        .filter(d => d.x >= 0 && d.y >= 0 && d.x <= frame.W && d.y <= frame.H);
      const r = await apiFetch(`/traffic-plans/${id}/ai-check`, { method: 'POST', body: JSON.stringify({ image, num: layout.num, workLen: layout.workLen,
        signs: signsNow(), crossings, devices, picture: { w: frame.W, h: frame.H }, signCodes: SIGN_CATALOG.map(([c]) => c) }) });
      if (r.note) addNote(r.note);
      checkFrame.current = frame;
      setCheck({ ...r, picks: (r.fixList || []).map(() => true) });
      setUndoFix(null);
    } catch (e) { setErr(e?.error || e?.message || 'The AI check did not work this time.'); }
    setBusy('');
  }
  // The AI check's fixes, applied to the drawing (only the ticked ones). The drawing as it was is kept for Undo.
  function applyFixes() {
    const fx = check?.fixList || [], unP = checkFrame.current?.unP;
    if (!fx.length || !unP) return;
    setUndoFix({ road: plan.road, moved: plan.moved || {}, extras, setup: plan.setup, side: plan.side, speed: plan.speed, road_type: plan.road_type, no_right_turn: plan.no_right_turn });
    const patch = {}, moved = { ...(plan.moved || {}) };
    let ex = [...extras], rd = road;
    fx.forEach((f, i) => {
      if (!check.picks[i]) return;
      if (f.kind === 'setting') patch[f.field] = f.field === 'no_right_turn' ? (f.value ? 1 : 0) : f.value;
      else if (f.kind === 'shift') rd = shiftRoad(rd, f.ft);
      else if (f.kind === 'remove') { if (ex.some(x => x.id === f.id)) ex = ex.filter(x => x.id !== f.id); else moved[f.id] = null; }
      else if (f.kind === 'move') { const ll = unP(f.x, f.y); if (ex.some(x => x.id === f.id)) ex = ex.map(x => (x.id === f.id ? { ...x, ll } : x)); else moved[f.id] = ll; }
      else if (f.kind === 'add') {
        const cat = f.type === 'sign' ? SIGN_CATALOG.find(([c]) => c.toLowerCase() === String(f.code || '').toLowerCase()) : null;
        if (f.type === 'sign' && !cat) return;
        ex.push({ id: newId(), type: f.type, ll: unP(f.x, f.y), ...(cat ? { code: cat[0], text: cat[1] } : null) });
      }
    });
    change({ ...patch, road: rd, moved, extras: ex });
    setCheck(c => ({ ...c, applied: true }));
  }
  const fits = layout && layout.reach <= layout.traced + 50;
  const reference = tcpReference(plan);
  const setupHint = plan.setup === 'shoulder'
    ? 'Trace the EDGE LINE beside the shoulder being closed'
    : 'Trace the CENTRE of the lane being closed';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={btn('#fff', '#111', '#e5e7eb')}>‹ Drawings</button>
        <input value={plan.title || ''} onChange={e => change({ title: e.target.value })} style={{ ...box, width: 360, fontSize: 16, fontWeight: 800 }} />
        <button onClick={aiCheck} disabled={!!busy} title="The AI looks over the finished plan before you print" style={{ ...btn('#fff', '#7c3aed', '#7c3aed'), marginLeft: 'auto' }}>{busy === 'check' ? 'Checking…' : '🤖 Check plan'}</button>
        <button onClick={print} disabled={busy === 'print'} style={btn('#1e3a5f')}>{busy === 'print' ? 'Making the PDF…' : '🖨️ Print PDF'}</button>
        <button onClick={async () => { if (window.confirm('Delete this drawing?')) { await apiFetch(`/traffic-plans/${id}`, { method: 'DELETE' }).catch(() => {}); onBack(); } }}
          style={btn('#fff', '#b91c1c', '#fecaca')}>Delete</button>
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 520px', minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') go(); }} placeholder="Find an address or intersection…" style={{ ...box, width: 300 }} />
            <button onClick={go} style={btn('#fff', '#1e3a5f', '#1e3a5f')}>{busy === 'search' ? '…' : 'Go'}</button>
            <span style={{ width: 1, height: 26, background: '#e5e7eb' }} />
            <button onClick={() => setMode(m => (m === 'area' ? null : 'area'))} style={mode === 'area' ? btn('#dc2626') : btn('#fff', '#dc2626', '#dc2626')}>① {mode === 'area' ? 'Done with work area' : 'Outline work area'}</button>
            <button onClick={() => setMode(m => (m === 'road' ? null : 'road'))} style={mode === 'road' ? btn('#2563eb') : btn('#fff', '#2563eb', '#2563eb')}>② {mode === 'road' ? 'Done tracing' : 'Trace the road'}</button>
            <button onClick={() => { setMode(null); aiPlan(); }} disabled={!!busy || area.length < 3} title={area.length < 3 ? 'Outline the work area first' : 'The AI picks the street and setup; the road is traced from the street map'}
              style={{ ...btn('#7c3aed'), opacity: area.length < 3 ? 0.45 : 1 }}>{busy === 'ai-map' ? 'Reading the street map…' : busy === 'ai' ? 'Thinking…' : '🤖 AI plan'}</button>
            {road.length >= 2 && (
              <button onClick={() => setMode(m => (m === 'roadpts' ? null : 'roadpts'))} title="Drag the blue points onto the lane line — everything follows"
                style={mode === 'roadpts' ? btn('#2563eb') : btn('#fff', '#2563eb', '#93c5fd')}>{mode === 'roadpts' ? 'Done moving points' : '✎ Move road points'}</button>
            )}
            <select value="" onChange={e => { const d = DEVICES.find(x => x.key === e.target.value); if (d) { setAdding(d); setMode('add'); setSel(null); } }}
              style={{ ...box, width: 150, fontWeight: 800, color: '#166534', borderColor: '#86efac' }}>
              <option value="">+ Add device…</option>
              <optgroup label="Devices">{DEVICES.filter(d => d.type !== 'sign').map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</optgroup>
              <optgroup label="Signs">{DEVICES.filter(d => d.type === 'sign').map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</optgroup>
            </select>
            {mode === 'add' && <button onClick={() => { setMode(null); setAdding(null); }} style={btn('#166534')}>Done adding</button>}
            {(area.length > 0 || road.length > 0 || extras.length > 0) && (
              <button onClick={() => { if (window.confirm('Clear the work area, the traced road and every device you moved, added or deleted?')) { change({ area: [], road: [], moved: {}, extras: [] }); setSel(null); } }} style={btn('#fff', '#6b7280', '#e5e7eb')}>Clear</button>
            )}
            {Object.keys(plan.moved || {}).length > 0 && <button onClick={() => change({ moved: {} })} title="Undo moves and deletes of the worked-out pieces (devices you added stay)" style={btn('#fff', '#6b7280', '#e5e7eb')}>↺ Put pieces back</button>}
          </div>
          <div style={{ fontSize: 12.5, color: mode ? '#111' : '#6b7280', marginBottom: 6, fontWeight: mode ? 700 : 400 }}>
            {mode === 'add' && adding ? `Click the map where the ${adding.type === 'sign' ? `${adding.code} ${adding.text} sign` : adding.label.toLowerCase()} goes — click again for another. Press Done adding when finished.`
              : mode === 'roadpts' ? 'Drag the blue points onto the line you traced for (lane centre, or the edge line for shoulder work). The signs and cones follow.'
              : mode === 'area' ? 'Click the corners of the work area on the map, then press Done.'
              : mode === 'road' ? `${setupHint}: click points starting well back from the work, in the direction traffic travels, and END where the work area starts. Then press Done.`
              : 'Zoom in on the site. ① Outline the work area. ② Trace the road traffic comes in on. Drag anything to move it; click a sign or cone to duplicate or delete it.'}
          </div>
          {selected && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '6px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13 }}>
              <b style={{ color: '#1e3a5f' }}>{deviceName(selected)}</b>
              {selected.extra && <span style={{ fontSize: 11.5, color: '#6b7280' }}>(added)</span>}
              <button onClick={duplicate} style={{ ...btn('#fff', '#1e3a5f', '#93c5fd'), padding: '4px 10px', marginLeft: 'auto' }}>⧉ Duplicate</button>
              <button onClick={remove} style={{ ...btn('#fff', '#b91c1c', '#fecaca'), padding: '4px 10px' }}>🗑 Delete</button>
              <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 15 }}>✕</button>
            </div>
          )}
          <div style={{ height: '68vh', borderRadius: 10, overflow: 'hidden', border: '1px solid #d1d5db', cursor: mode ? 'crosshair' : undefined }}>
            <MapContainer center={center} zoom={plan.zoom || 17} maxZoom={20} style={{ height: '100%', width: '100%' }}>
              <TileLayer attribution="Imagery &copy; Esri, Maxar, Earthstar Geographics" url={IMAGERY} maxNativeZoom={19} maxZoom={20} />
              <MapGrab onMap={m => { mapRef.current = m; }} onView={(c, z) => change({ center_lat: c[0], center_lng: c[1], zoom: z })} />
              <ClickCatcher mode={mode === 'roadpts' ? null : mode} onPoint={addPoint} onEmpty={() => setSel(null)} />
              {area.length >= 2 && <Polygon positions={area} pathOptions={{ color: '#dc2626', weight: 2, dashArray: '6 4', fillColor: '#dc2626', fillOpacity: 0.28 }} />}
              {mode === 'area' && area.map((p, i) => <Marker key={`a${i}`} position={p} icon={icons.dot} />)}
              {road.length >= 2 && <Polyline positions={road} pathOptions={{ color: '#60a5fa', weight: 2, dashArray: '4 6' }} />}
              {(mode === 'road' || mode === 'roadpts') && road.map((p, i) => (
                <Marker key={`r${i}`} position={p} icon={icons.dot} draggable={mode === 'roadpts'}
                  eventHandlers={{ dragend: e => { const q = e.target.getLatLng(); change({ road: road.map((x, j) => (j === i ? [q.lat, q.lng] : x)) }); } }} />
              ))}
              {placed.map(it => (
                <Marker key={it.id} position={it.ll} draggable={mode !== 'roadpts'} zIndexOffset={it.id === sel ? 1000 : it.type === 'sign' ? 200 : 0}
                  icon={iconFor(it, it.id === sel, plan.zoom || 17)}
                  eventHandlers={{ click: () => setSel(it.id), dragstart: () => setSel(it.id), dragend: e => { const q = e.target.getLatLng(); moveDevice(it, [q.lat, q.lng]); } }}
                  title={it.type === 'sign' ? `${it.code} — ${it.text}` : it.text || ''} />
              ))}
            </MapContainer>
          </div>
        </div>

        <div style={{ width: 300, flexShrink: 0, background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '6px 14px 14px' }}>
          {/* Which standard this drawing is to (Ryan, Nov 2 2026) — the drawing asks, and the print names it. */}
          <div style={lbl}>Standard — which book is this drawing to?</div>
          {!plan.standard && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '7px 10px', fontSize: 12.5, color: '#92400e', marginBottom: 6 }}>
              Pick <b>ATSSA</b> or the <b>NDOT Standard Plans</b> before printing — the plan names the standard and the typical or sheet it follows.
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            {Object.entries(STANDARDS).map(([k, s]) => (
              <button key={k} onClick={() => change({ standard: k, standard_ref: '' })} style={{ ...(plan.standard === k ? btn('#1e3a5f') : btn('#fff', '#1e3a5f', '#cbd5e1')), flex: 1 }}>{s.label}</button>
            ))}
          </div>
          {reference && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#374151', lineHeight: 1.45 }}>
              Using <b>{reference.name}</b>
              <input value={plan.standard_ref || ''} onChange={e => change({ standard_ref: e.target.value })}
                placeholder={reference.refDefault || 'Sheet number, e.g. T-…'} title="The typical application or sheet named on the print — type over the default if you use another"
                style={{ ...box, marginTop: 4 }} />
              <div style={{ fontSize: 11.5, color: reference.needsRef ? '#b45309' : '#6b7280', marginTop: 2 }}>
                {reference.needsRef ? 'Type the NDOT sheet number this drawing follows — it prints as the reference.' : `Prints as: ${reference.ref}`}
              </div>
            </div>
          )}
          <div style={lbl}>Job</div>
          <JobSearchSelect value={plan.project_id || ''} projects={jobs} onChange={pickJob} placeholder="Type a job number or name…" />
          {plan.project_id && <button onClick={() => pickJob('')} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer', padding: '3px 0' }}>✕ No job</button>}
          <div style={lbl}>Location (prints on the plan)</div>
          <input value={plan.location || ''} onChange={e => change({ location: e.target.value })} placeholder="Street / cross street, city" style={box} />
          <div style={lbl}>Work date</div>
          <input type="date" value={plan.work_date || ''} onChange={e => change({ work_date: e.target.value })} style={box} />
          <div style={lbl}>Setup</div>
          <select value={plan.setup || 'lane_closed'} onChange={e => change({ setup: e.target.value, moved: {} })} style={box}>
            {Object.entries(SETUPS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {['right', 'left'].map(s => (
              <button key={s} onClick={() => change({ side: s, moved: {} })} style={{ ...(plan.side === s ? btn('#1e3a5f') : btn('#fff', '#1e3a5f', '#cbd5e1')), flex: 1 }}>
                {s === 'right' ? 'Right side' : 'Left side'}
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!plan.no_right_turn} onChange={e => change({ no_right_turn: e.target.checked ? 1 : 0 })} />
            No Right Turn sign (R3-1)
          </label>
          {!!plan.no_right_turn && <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>Set where the work starts — drag it to the street or driveway that turns into the work.</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={lbl}>Speed (mph)</div>
              <select value={plan.speed || 35} onChange={e => change({ speed: Number(e.target.value), moved: {} })} style={box}>
                {SPEEDS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={lbl}>{plan.setup === 'shoulder' ? 'Shoulder (ft)' : 'Lane (ft)'}</div>
              <input type="number" min="4" max="20" value={plan.setup === 'shoulder' ? (plan.shoulder_width ?? 8) : (plan.lane_width ?? 12)}
                onChange={e => change({ [plan.setup === 'shoulder' ? 'shoulder_width' : 'lane_width']: e.target.value, moved: {} })} style={box} />
            </div>
          </div>
          <div style={lbl}>Road type (sign spacing)</div>
          <select value={plan.road_type || 'urban_low'} onChange={e => change({ road_type: e.target.value, moved: {} })} style={box}>
            {Object.entries(ROAD_TYPES).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
          </select>

          {layout ? (
            <div style={{ marginTop: 12, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.6 }}>
              <b style={{ color: '#1e3a5f' }}>Worked out</b><br />
              {plan.setup === 'shoulder' ? 'Shoulder taper' : 'Taper'} <b>{layout.num.taper}'</b> · Buffer <b>{layout.num.buffer}'</b><br />
              Signs A <b>{layout.num.A}'</b> · B <b>{layout.num.B}'</b>{layout.signs.length > 2 ? <> · C <b>{layout.num.C}'</b></> : null}<br />
              Cones every <b>{layout.num.coneTaper}'</b> in the taper, <b>{layout.num.coneTangent}'</b> along the work<br />
              Work area <b>{layout.workLen}'</b> · reaches <b>{layout.reach}'</b> back
              {!fits && <div style={{ color: '#b45309', fontWeight: 700 }}>The layout reaches past what you traced ({layout.traced}') — trace further back so the signs follow the road.</div>}
            </div>
          ) : (
            <div style={{ marginTop: 12, fontSize: 12.5, color: '#9ca3af' }}>Trace the road (at least two points) and the signs and cones appear.</div>
          )}

          {ai && (
            <div style={{ marginTop: 12, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <b style={{ color: '#6d28d9' }}>🤖 AI plan</b>
                {ai.confidence && <span style={{ marginLeft: 6, fontSize: 11, color: '#7c3aed' }}>({ai.confidence} confidence)</span>}
                <button onClick={() => setAi(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>✕</button>
              </div>
              <div>{ai.streetName}{ai.direction ? ` · ${ai.direction}` : ''} · {(SETUPS[ai.setup] || {}).label} · {ai.side} side · {ai.speed} mph{ai.no_right_turn ? ' · No Right Turn' : ''}</div>
              {ai.why && <div style={{ color: '#374151', marginTop: 3 }}>{ai.why}</div>}
              {!ai.traced && <div style={{ color: '#b45309', fontWeight: 700, marginTop: 3 }}>Could not trace the road from the street map — trace it by hand (②).</div>}
              {!ai.fits_our_setups && <div style={{ color: '#b91c1c', fontWeight: 700, marginTop: 3 }}>⚠ This job needs more than these two setups — see below.</div>}
              {(ai.warnings || []).map((w, i) => <div key={i} style={{ color: '#b45309', marginTop: 2 }}>⚠ {w}</div>)}
              {ai.note && <div style={{ color: '#6b7280', marginTop: 3, fontSize: 11.5 }}>Added to Extra notes: “{ai.note}”</div>}
              <div style={{ color: '#9ca3af', marginTop: 4, fontSize: 11 }}>Look it over on the map and change anything, then 🤖 Check plan before you print.</div>
            </div>
          )}
          {check && (
            <div style={{ marginTop: 10, background: check.looks_good ? '#f0fdf4' : '#fffbeb', border: `1px solid ${check.looks_good ? '#bbf7d0' : '#fde68a'}`, borderRadius: 8, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <b style={{ color: check.looks_good ? '#15803d' : '#b45309' }}>{check.looks_good ? '✓ AI check: looks good' : '⚠ AI check: have a look'}</b>
                <button onClick={() => setCheck(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>✕</button>
              </div>
              {check.summary && <div style={{ color: '#374151' }}>{check.summary}</div>}
              {(check.warnings || []).map((w, i) => <div key={i} style={{ color: '#b45309', marginTop: 2 }}>⚠ {w}</div>)}
              {check.note && <div style={{ color: '#6b7280', marginTop: 3, fontSize: 11.5 }}>Added to Extra notes: “{check.note}”</div>}
              {(check.fixList || []).length > 0 && (
                <div style={{ marginTop: 6, borderTop: '1px solid #fde68a', paddingTop: 6 }}>
                  <b style={{ color: '#6d28d9' }}>🤖 Fixes it suggests</b>
                  {check.fixList.map((f, i) => (
                    <label key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 3, cursor: check.applied ? 'default' : 'pointer', opacity: check.applied && !check.picks[i] ? 0.5 : 1 }}>
                      <input type="checkbox" checked={!!check.picks[i]} disabled={check.applied}
                        onChange={e => setCheck(c => ({ ...c, picks: c.picks.map((v, j) => (j === i ? e.target.checked : v)) }))} style={{ marginTop: 3 }} />
                      <span>{f.label}{f.why ? <span style={{ color: '#6b7280' }}> — {f.why}</span> : null}</span>
                    </label>
                  ))}
                  {!check.applied
                    ? <button onClick={applyFixes} disabled={!check.picks.some(Boolean)} style={{ ...btn('#7c3aed'), marginTop: 6, width: '100%' }}>✓ Apply the ticked fixes</button>
                    : <div style={{ color: '#15803d', fontWeight: 700, marginTop: 6 }}>Applied — look it over on the map, then 🤖 Check plan again.</div>}
                  {check.applied && undoFix && (
                    <button onClick={() => { change(undoFix); setUndoFix(null); setCheck(c => ({ ...c, applied: false })); }} style={{ ...btn('#fff', '#6b7280', '#e5e7eb'), marginTop: 6, width: '100%' }}>↺ Undo the AI fixes</button>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={lbl}>Prepared by (ATSSA certified)</div>
          <input value={plan.prepared_by || ''} onChange={e => change({ prepared_by: e.target.value })} placeholder="Name" style={box} />
          <input value={plan.cert_no || ''} onChange={e => change({ cert_no: e.target.value })} placeholder="ATSSA certification #" style={{ ...box, marginTop: 6 }} />
          <div style={lbl}>Extra notes (print under the standard notes)</div>
          <textarea value={plan.notes || ''} onChange={e => change({ notes: e.target.value })} rows={3} placeholder="Work hours, flagger stations, detours…" style={{ ...box, resize: 'vertical' }} />
        </div>
      </div>
    </div>
  );
}

// ── The picture for the PDF ──────────────────────────────────────────────────────────────────────
// Always the same size, whatever the screen: framed so the work area and every sign and cone fit,
// at the sharpest zoom that holds them all, with the satellite underneath, the pieces drawn on, and a
// north arrow and scale bar.
const SNAP_W = 1560, SNAP_H = 1200;
// The satellite under a fixed-size picture, framed so all of `pts` fit at the sharpest zoom (≤19).
async function satellite(map, pts, W, H) {
  if (!map) throw new Error('The map is not ready yet.');
  let zz = Math.min(map.getZoom() + 1, 19), mid = map.getCenter();
  if (pts.length) {
    const b = L.latLngBounds(pts.map(ll => L.latLng(ll[0], ll[1])));
    mid = b.getCenter();
    for (zz = 19; zz > 10; zz--) {
      const a = map.project(b.getNorthWest(), zz), c = map.project(b.getSouthEast(), zz);
      if (Math.abs(c.x - a.x) <= W * 0.86 && Math.abs(c.y - a.y) <= H * 0.86) break;
    }
  }
  const cpt = map.project(mid, zz);
  const origin = L.point(cpt.x - W / 2, cpt.y - H / 2);
  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  g.fillStyle = '#d1d5db'; g.fillRect(0, 0, W, H);
  const T = 256, x0 = Math.floor(origin.x / T), y0 = Math.floor(origin.y / T), x1 = Math.floor((origin.x + W) / T), y1 = Math.floor((origin.y + H) / T);
  const loads = [];
  for (let tx = x0; tx <= x1; tx++) for (let ty = y0; ty <= y1; ty++) {
    loads.push(new Promise(done => {
      const im = new Image(); im.crossOrigin = 'anonymous';
      im.onload = () => { g.drawImage(im, tx * T - origin.x, ty * T - origin.y, T, T); done(); };
      im.onerror = () => done();
      im.src = IMAGERY.replace('{z}', zz).replace('{y}', ty).replace('{x}', tx);
    }));
  }
  await Promise.all(loads);
  const P = ll => { const p = map.project(L.latLng(ll[0], ll[1]), zz); return [p.x - origin.x, p.y - origin.y]; };
  // …and back: a spot on the picture → [lat, lng] (for the AI check's fixes, which point at the picture).
  const unP = (x, y) => { const q = map.unproject(L.point(x + origin.x, y + origin.y), zz); return [q.lat, q.lng]; };
  return { canvas, g, P, unP, zz, mid };
}
function northArrow(g, W, s) {
  g.fillStyle = 'rgba(255,255,255,0.9)'; g.fillRect(W - 46 * s, 10 * s, 36 * s, 44 * s);
  g.fillStyle = '#111'; g.beginPath(); g.moveTo(W - 28 * s, 16 * s); g.lineTo(W - 36 * s, 36 * s); g.lineTo(W - 20 * s, 36 * s); g.closePath(); g.fill();
  g.font = `800 ${12 * s}px sans-serif`; g.textAlign = 'center'; g.textBaseline = 'alphabetic'; g.fillText('N', W - 28 * s, 50 * s);
}
function drawArea(g, P, area, s) {
  if (area.length < 3) return;
  g.beginPath(); area.forEach((ll, i) => { const [x, y] = P(ll); if (i) g.lineTo(x, y); else g.moveTo(x, y); }); g.closePath();
  g.fillStyle = 'rgba(220,38,38,0.32)'; g.fill(); g.setLineDash([8 * s, 5 * s]); g.lineWidth = 2.5 * s; g.strokeStyle = '#dc2626'; g.stroke(); g.setLineDash([]);
  const c = area.reduce((a, ll) => [a[0] + ll[0] / area.length, a[1] + ll[1] / area.length], [0, 0]); const [cx, cy] = P(c);
  tag(g, 'WORK AREA', cx, cy, '#dc2626', '#fff', 12 * s);
}

// The picture the 🤖 AI plan looks at: the work area with ~300 ft around it, and the nearby streets'
// centre lines from the street map drawn in yellow with their numbers (matching the list it is sent).
async function aiPicture(map, area, near, ways) {
  const W = 1280, H = 1000, s = 1.3;
  const [clat, clng] = area.reduce((a, ll) => [a[0] + ll[0] / area.length, a[1] + ll[1] / area.length], [0, 0]);
  const dLat = 300 / 364000, dLng = 300 / (364000 * Math.cos((clat * Math.PI) / 180));
  const { canvas, g, P } = await satellite(map, [...area, [clat + dLat, clng - dLng], [clat - dLat, clng + dLng]], W, H);
  const [cx, cy] = P([clat, clng]);
  for (const st of near) {
    const mine = ways.filter(w => (w.tags.name || w.tags.ref || `#${w.id}`) === (st.key || st.name) || w.id === st.wayId);
    let best = null;
    g.strokeStyle = 'rgba(250,204,21,0.9)'; g.lineWidth = 3 * s;
    for (const w of mine) {
      g.beginPath();
      w.geom.forEach((ll, i) => { const [x, y] = P(ll); if (i) g.lineTo(x, y); else g.moveTo(x, y); });
      g.stroke();
      // The number goes on the street ~190 px from the work area, so the WORK AREA tag never hides it.
      for (let i = 1; i < w.geom.length; i++) {
        const [ax, ay] = P(w.geom[i - 1]), [bx, by] = P(w.geom[i]), steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / 15));
        for (let k = 0; k <= steps; k++) {
          const x = ax + ((bx - ax) * k) / steps, y = ay + ((by - ay) * k) / steps;
          if (x < 30 || y < 30 || x > W - 30 || y > H - 30) continue;
          const d = Math.abs(Math.hypot(x - cx, y - cy) - 190);
          if (!best || d < best.d) best = { d, x, y };
        }
      }
    }
    if (best) tag(g, String(st.n), best.x, best.y, '#facc15', '#111', 15 * s);
  }
  drawArea(g, P, area, s);
  northArrow(g, W, s);
  return canvas.toDataURL('image/jpeg', 0.85);
}

async function snapshot(map, area, placed, frame) {   // frame (optional) receives P and unP for this picture
  const W = SNAP_W, H = SNAP_H;
  const f = 1.6;   // drawn pieces are sized for a picture this big
  const { canvas, g, P, unP, zz, mid } = await satellite(map, [...area, ...placed.map(it => it.ll)], W, H);
  if (frame) Object.assign(frame, { P, unP, W, H });
  const s = f;
  drawArea(g, P, area, s);
  for (const it of placed) {
    const [x, y] = P(it.ll);
    if (it.type === 'cone') {
      g.beginPath(); g.arc(x, y, 5 * s, 0, Math.PI * 2); g.fillStyle = '#f97316'; g.fill(); g.lineWidth = 1.5 * s; g.strokeStyle = '#fff'; g.stroke();
    } else if (it.type === 'arrow') {
      g.fillStyle = '#111'; g.fillRect(x - 22 * s, y - 8 * s, 44 * s, 16 * s); g.strokeStyle = '#facc15'; g.lineWidth = 1.5 * s; g.strokeRect(x - 22 * s, y - 8 * s, 44 * s, 16 * s);
      g.fillStyle = '#facc15'; g.font = `800 ${11 * s}px sans-serif`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('>>>', x, y);
    } else if (it.type === 'drum') {
      g.beginPath(); g.arc(x, y, 7 * s, 0, Math.PI * 2); g.fillStyle = '#f97316'; g.fill(); g.lineWidth = 2.5 * s; g.strokeStyle = '#fff'; g.beginPath(); g.arc(x, y, 4 * s, 0, Math.PI * 2); g.stroke();
      g.beginPath(); g.arc(x, y, 7 * s, 0, Math.PI * 2); g.lineWidth = 1.2 * s; g.strokeStyle = '#111'; g.stroke();
    } else if (it.type === 'barricade') {
      const w = 34 * s, h = 10 * s;
      g.save(); g.beginPath(); g.rect(x - w / 2, y - h / 2, w, h); g.clip();
      const band = 5 * s;   // slanted orange and white bands
      for (let i = 0, k = -h; k < w + h; i++, k += band) {
        g.fillStyle = i % 2 ? '#fff' : '#f97316';
        g.beginPath(); g.moveTo(x - w / 2 + k, y + h / 2); g.lineTo(x - w / 2 + k + band, y + h / 2); g.lineTo(x - w / 2 + k + band + h, y - h / 2); g.lineTo(x - w / 2 + k + h, y - h / 2); g.fill();
      }
      g.restore(); g.lineWidth = 1.5 * s; g.strokeStyle = '#111'; g.strokeRect(x - w / 2, y - h / 2, w, h);
    } else if (it.type === 'sign') {
      drawSign(g, it.code, it.text, x, y, 62 * s);
    } else if (it.type === 'label') {
      tag(g, it.text, x, y, 'rgba(255,255,255,0.93)', '#1e3a5f', 11 * s);
    }
  }
  // North arrow (the map is always north-up) and a scale bar.
  northArrow(g, W, s);
  const lat = mid.lat, ftPerPx = (156543.03392 * Math.cos((lat * Math.PI) / 180) / Math.pow(2, zz)) * 3.28084;
  const nice = [50, 100, 200, 250, 500, 1000, 2000].find(v => v / ftPerPx >= 90 * s) || 2000, bar = nice / ftPerPx;
  g.fillStyle = 'rgba(255,255,255,0.9)'; g.fillRect(10 * s, H - 34 * s, bar + 20 * s, 24 * s);
  g.fillStyle = '#111'; g.fillRect(20 * s, H - 22 * s, bar, 5 * s);
  g.font = `700 ${11 * s}px sans-serif`; g.textAlign = 'left'; g.fillText(`${nice} ft`, 20 * s, H - 25 * s);
  g.font = `${9 * s}px sans-serif`; g.textAlign = 'right'; g.fillStyle = 'rgba(255,255,255,0.95)'; g.fillText('Imagery: Esri, Maxar, Earthstar Geographics', W - 6 * s, H - 6 * s);
  return canvas.toDataURL('image/jpeg', 0.9);
}
function tag(g, text, x, y, bg, fg, size, align = 'center') {
  g.font = `800 ${size}px sans-serif`;
  const w = g.measureText(text).width + size * 0.8, h = size * 1.5;
  const left = align === 'left' ? x : x - w / 2;
  g.fillStyle = bg; g.fillRect(left, y - h / 2, w, h); g.strokeStyle = fg; g.lineWidth = 1; g.strokeRect(left, y - h / 2, w, h);
  g.fillStyle = fg; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(text, left + size * 0.4, y);
}
